import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { SEOHead } from "@/components/seo/SEOHead";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";

export default function TermsOfService() {
  return (
    <>
      <SEOHead 
        title="Terms of Service | Aiveliq360"
        description="Read the Terms of Service for Aiveliq360's AI Sales Operating System. Understand your rights and responsibilities when using our platform."
        canonicalUrl="https://aiveliq360.com/terms"
      />
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border py-3">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={aiveliqLogo} alt="Aiveliq360" className="h-6 object-contain" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-1.5">
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Home
              </Button>
            </Link>
          </div>
        </nav>

        <main className="max-w-4xl mx-auto px-4 py-12">
          <h1 className="text-3xl font-bold mb-8">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Last updated: December 11, 2025</p>

          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4">1. Agreement to Terms</h2>
              <p className="text-muted-foreground">
                By accessing or using Aiveliq360's AI Sales Operating System ("Service"), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground">
                Aiveliq360 provides an AI-powered sales operating system that includes lead generation, sales engagement workflows, coaching, revenue intelligence, and workflow automation. The Service is provided "as is" and "as available."
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">3. User Accounts</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>You must provide accurate, complete, and current information</li>
                <li>You are responsible for safeguarding your account credentials</li>
                <li>You must notify us immediately of any unauthorized access</li>
                <li>You may not share your account with others without authorization</li>
                <li>One person or legal entity may not maintain more than one free account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">4. Subscription and Payment</h2>
              <h3 className="text-lg font-medium mb-2">4.1 Billing</h3>
              <p className="text-muted-foreground mb-4">
                Paid plans are billed in advance on a monthly or annual basis. All fees are non-refundable except as required by law.
              </p>
              
              <h3 className="text-lg font-medium mb-2">4.2 Plan Changes</h3>
              <p className="text-muted-foreground mb-4">
                You may upgrade or downgrade your plan at any time. Upgrades take effect immediately; downgrades take effect at the end of the current billing period.
              </p>

              <h3 className="text-lg font-medium mb-2">4.3 Cancellation</h3>
              <p className="text-muted-foreground">
                You may cancel your subscription at any time. Upon cancellation, you will have access until the end of your current billing period.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">5. Acceptable Use</h2>
              <p className="text-muted-foreground mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Use the Service for any unlawful purpose</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit malware, viruses, or harmful code</li>
                <li>Attempt to gain unauthorized access to systems</li>
                <li>Interfere with the Service's proper functioning</li>
                <li>Scrape, data mine, or collect data without permission</li>
                <li>Use the Service to send unsolicited communications (spam)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">6. Intellectual Property</h2>
              <p className="text-muted-foreground">
                The Service and its original content, features, and functionality are owned by Aiveliq360 and are protected by international copyright, trademark, and other intellectual property laws. You retain ownership of your data.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">7. Data and Privacy</h2>
              <p className="text-muted-foreground">
                Your use of the Service is also governed by our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>. By using the Service, you consent to our collection and use of data as described therein.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">8. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                To the maximum extent permitted by law, Aiveliq360 shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities, arising from your use of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">9. Disclaimer of Warranties</h2>
              <p className="text-muted-foreground">
                The Service is provided "AS IS" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">10. Indemnification</h2>
              <p className="text-muted-foreground">
                You agree to indemnify and hold harmless Aiveliq360 and its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of the Service or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">11. Termination</h2>
              <p className="text-muted-foreground">
                We may terminate or suspend your account and access to the Service immediately, without prior notice, for conduct that we determine violates these Terms or is harmful to other users, us, or third parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">12. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We reserve the right to modify these Terms at any time. We will provide notice of significant changes. Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">13. Governing Law</h2>
              <p className="text-muted-foreground">
                These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">14. Contact Us</h2>
              <p className="text-muted-foreground">
                For questions about these Terms, contact us at:<br />
                <strong>Email:</strong> legal@aiveliq360.com
              </p>
            </section>
          </div>
        </main>

        <footer className="border-t border-border py-8">
          <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <img src={aiveliqLogo} alt="Aiveliq360" className="h-6 object-contain" />
            <nav className="flex items-center gap-4 text-xs text-muted-foreground">
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="text-foreground">Terms</Link>
              <Link to="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
            </nav>
            <p className="text-xs text-muted-foreground">© 2025 Aiveliq360</p>
          </div>
        </footer>
      </div>
    </>
  );
}
